import { useContext, useEffect, useState } from 'react'
import ElectronIPC from '@/services/ElectronIPC'
import * as Constants from '@/services/Constants'
import { Button } from '@/UIComponents2/Button'
import TutorialIcon from '@/CustomIcons/TutorialIcon'
import { useIsProUser } from '@/services/useIsProUser'
import { ProButton } from '../Buttons/ProButton'
import { AudioSettings } from './internal/audio/AudioSettings'
import { Trigger } from '@/utlis/Trigger'
import { Dialog } from '@/components/Dialog'
import { VideoSettings } from './internal/video/VideoSettings'
import { RecordingSettings } from './internal/recording/RecordingSettings'
import { MidiSettings } from './internal/midi/MidiSettings'
import { SessionSettings as SessionSettingsTab } from './internal/session/SessionSettings'
import DeveloperSettings from './internal/developer/DeveloperSettings'
import { useAppContext } from '@/AppContext'
import useDeveloperOptionsActions from '@/functionalities/developer-options/useDeveloperOptionsActions'
import { StudioSettings } from './internal/studios/StudioSettings'
import { Statistics } from './internal/statistics/Statistics'
import RootSnackbarRenderer from '@/pages/RootSnackbarRenderer'
import { useTranslation } from 'react-i18next'
import Account from './internal/account/Account'
import { VerticalTabs } from '../VerticalTabs'
import { useRoomContext } from '@/RoomContext'
import { PortalContext } from '@/UIComponents2/PortalContext'

export type SettingsTab = 'audio' | 'video' | 'midi' | 'recording' | 'session' | 'studios' | 'stats' | 'account' | 'dev'
export type SettingsLocation = {
    tab: SettingsTab
    section?: string
}

type SettingsProps = {
    initialTab?: SettingsTab
    scrollToSection?: string
    inSession?: boolean
}

export function SessionSettings(props: SettingsProps) {
    const { t } = useTranslation()
    const [appState] = useAppContext()
    const [roomState] = useRoomContext()
    const { setDeveloperMode } = useDeveloperOptionsActions()
    const [tab, setTab] = useState<SettingsTab>(props.initialTab ?? 'audio')
    const inSession = props.inSession ?? true
    const content = renderContent(tab, inSession)
    const portalContainer = useContext(PortalContext)

    const scrollToAnchor = (id: string) => {
        if (portalContainer) {
            portalContainer.querySelector(`#${id}`)?.scrollIntoView({
                behavior: 'smooth',
            })
        } else {
            document.getElementById(id)?.scrollIntoView({
                behavior: 'smooth',
            })
        }
    }

    useEffect(() => {
        setTab(props.initialTab ?? 'audio')
        if (props.scrollToSection) {
            setTimeout(() => scrollToAnchor(props.scrollToSection!), 100)
        }
    }, [props.initialTab, props.scrollToSection])

    useEffect(() => {
        const h = (ev: KeyboardEvent) => {
            if (ev.key === 'D') {
                ev.preventDefault()
                setDeveloperMode(true)
            }
        }
        document.addEventListener('keydown', h)
        return () => document.removeEventListener('keydown', h)
    }, [])

    const tabs = getTabs(inSession, appState.appState.isInDevMode, roomState.isSubroomsEnabled)

    return (
        <div className="h-full flex flex-col gap-[10px] md:gap-[35px]">
            <div className="absolute top-0 left-0">
                <RootSnackbarRenderer />
            </div>

            <TopBar />

            <VerticalTabs
                tabs={tabs}
                currentTab={tab}
                onTabChange={(tab) => setTab(tab as SettingsTab)}
                mobileSuffix={t('Settings')}
            >
                {content}
            </VerticalTabs>

            <MayNeedRestartDialog />
        </div>
    )
}

export const mayNeedRestartTrigger = new Trigger<void>()
function MayNeedRestartDialog() {
    const [isOpen, setIsOpen] = useState(false)

    useEffect(() => {
        const h = () => {
            setIsOpen(true)
        }
        mayNeedRestartTrigger.on('trigger', h)
        return () => {
            mayNeedRestartTrigger.off('trigger', h)
        }
    }, [])

    return (
        <Dialog zIndex={21} portaled={false} open={isOpen} onClose={() => setIsOpen(false)} title="Session Settings">
            <div className="t-lg px-8">You may need to restart the app for these settings changes to take effect.</div>
            <div className="pt-6">
                <Button className="w-full h-[44px]" variant="primary" onClick={() => setIsOpen(false)}>
                    OK
                </Button>
            </div>
        </Dialog>
    )
}

function renderContent(tab: SettingsTab, inSession: boolean) {
    if (tab === 'audio') {
        return <AudioSettings />
    }
    if (tab === 'video') {
        return <VideoSettings inSession={inSession} />
    }
    if (tab === 'midi') {
        return <MidiSettings inSession={inSession} />
    }
    if (tab === 'recording') {
        return <RecordingSettings inSession={inSession} />
    }
    if (tab === 'session') {
        return <SessionSettingsTab />
    }
    if (tab === 'studios') {
        return <StudioSettings />
    }
    if (tab === 'stats') {
        return <Statistics />
    }
    if (tab === 'account') {
        return <Account />
    }
    if (tab === 'dev') {
        return <DeveloperSettings />
    }
}

function TopBar() {
    const { t } = useTranslation()
    const isProUser = useIsProUser()

    const handleOpenTutorials = () => {
        ElectronIPC.openLinkInBrowser(Constants.links.zendesk_tutorials)
    }

    return (
        <div className="flex justify-between items-center pr-[70px] pl-[20px] ">
            <h2 className="t-title-dialog">Session Settings</h2>
            <div className="hidden md:flex items-center gap-8">
                <Button
                    onClick={() => handleOpenTutorials()}
                    className="flex gap-2 items-center opacity-75 hover:opacity-100"
                >
                    <TutorialIcon />
                    Tutorials
                </Button>
                {!isProUser && <ProButton>{t('Upgrade to Pro')}</ProButton>}
            </div>
        </div>
    )
}

function getTabs(inSession: boolean, devMode: boolean, subroomsEnabled: boolean) {
    let tabs
    if (inSession) {
        tabs = [
            { id: 'audio', label: 'Audio' },
            { id: 'video', label: 'Video' },
            { id: 'recording', label: 'Recording' },
            { id: 'midi', label: 'MIDI' },
            { id: 'session', label: 'Session' },
            ...(subroomsEnabled ? [{ id: 'studios', label: 'Studios' }] : []),
            { id: 'stats', label: 'Statistics' },
            { id: 'account', label: 'Account' },
        ]
    } else {
        tabs = [
            { id: 'audio', label: 'Audio' },
            { id: 'video', label: 'Video' },
            { id: 'recording', label: 'Recording' },
            { id: 'midi', label: 'MIDI' },
            { id: 'account', label: 'Account' },
        ]
    }

    if (devMode) {
        tabs.push({ id: 'dev', label: 'Developer' })
    }
    return tabs
}
